/**
 * DO NOT MODIFY THIS FILE (Unless you really really have to).
 *
 * The Design System team is working on standardizing theming within the
 * Designer (and Editor).
 *
 * We're going to provide clear boundaries, mechanisms, and guidelines
 * to help folks understand the space and to be able to make changes with
 * confidence.
 *
 * In the meantime, if you really have to make changes, please reach
 * out to us!
 *
 * @since 2022-02-07
 * @link #design-system (Slack)
 * @see https://webflow.atlassian.net/browse/PDTY-2171
 * @see https://docs.google.com/document/d/1OOHFaJhsxbJVqXT7Zxk_Z79yzvpeGiw1mMqXua3NMAI
 *
 * This file was updated with the new values for Design 2.0.
 * @since 2023-11-02
 * @see https://webflow.atlassian.net/browse/PDTY-6815
 */

import * as React from 'react';
import {
  black as blacken,
  shadow,
  fade,
} from '@packages/utilities/inline-style-utils';

import * as Z_INDEXES from './zindex';
import SmallMarkIcon from './legacy/icons/CheckMedium';
import DashIcon from './legacy/icons/Indeterminate';
import ExternalLink from './legacy/icons/NewTabSmallThin';
import {
  palette as paletteNormal,
  tokens,
} from '@packages/utilities/styled-tokens';
import {rgba} from '@packages/systems/core/utils/ColorUtils';

import type {Theme} from './Theme';

/**
 * Adjusted for the Designer refinement, round 2 update.
 * 12px -> 12.5px
 * 11px -> 11.5px
 *
 * @see https://webflow.atlassian.net/browse/PDTY-6686
 * 2023-09-21
 */
const bodyFontSize = 11.5;
const headingFontSize = 11.5;
const titleFontSize = 13;
const bodyLetterSpacing = '-0.115px';

export const buildTheme = (palette: typeof paletteNormal): Theme => {
  const {
    white,
    whiteFade05,
    whiteFade20,
    whiteFade90,
    black,
    blackFade05,
    blackFade30,
    blackFade48,
    blueFade10,
    redFade10,
    gray50,
    gray100,
    gray200,
    gray300,
    gray400,
    gray500,
    gray600,
    gray700,
    gray800,
    gray900,
    blue100,
    blue200,
    blue300,
    blue400,
    blue600,
    purple100,
    purple300,
    purple400,
    pink300,
    pink400,
    pink600,
    red100,
    red200,
    red300,
    red400,
    orange100,
    orange200,
    orange300,
    yellow300,
    yellow400,
    yellow500,
    green100,
    green200,
    green300,
    green400,
  } = palette.colors;

  const colors = {
    white,
    black,
    /**
     * Text color on regular backgrounds (e.g. Panel). Used for primary text.
     */
    accentForeground: tokens.colors.blueText,
    /**
     * Icon and border colors. Elements that do not contain content.
     * Used for primary UI.
     */
    accent: tokens.colors.blueBorder,
    /**
     * Background and text elements that render specifically in the Webflow
     * Designer canvas area. Used for primary UI.
     *
     * ---
     *
     * Warning: This color is used in a computed value. Until that
     * call-site is refactored, this has to remain a hex color.
     *
     * @since 2023-11-03
     */
    accentMask: blue300,
    /**
     * Background for interactive elements (e.g Button). Used for primary UI.
     * Also handles :active state.
     */
    accentBackground: tokens.colors.blueBackground,
    /**
     * Background for selected nodes in Navigator
     */
    accentNavigatorBackground: tokens.colors.blueTransparent,
    accentNavigatorText: tokens.colors.blueText,
    /**
     * Avoid using this one.
     * Use `colors.accentForeground` instead.
     *
     * Text color on regular backgrounds (e.g. Panel). Used for primary text.
     */
    accentVivid: tokens.colors.blueText,
    /**
     * Avoid using this one.
     * Use `colors.accent` instead.
     *
     * Icon and border colors. Elements that do not contain content.
     *
     * Hex value is required for computed flashing effects:
     * @see public/js/designer-flux/components/ui/TimelineActionList.jsx
     */
    accentPrimary: tokens.colors.blueText,
    /**
     * Avoid using this one.
     * Use `colors.sym` instead.
     *
     * Background for interactive elements (e.g Button). Used for primary UI.
     * Also handles :active state.
     */
    accentBright: tokens.colors.blueBackground,
    /**
     * Avoid using this one.
     *
     * Used only in the Onboarding (Tutorial) Popover button border.
     */
    accentBorder: blue600,
    /**
     * Icon and border colors. Elements that do not contain content.
     * Used to indicate draft UI within the Webflow Designer.
     */
    accentAlt: orange200,
    /**
     * Specifically used for a Callout UI.
     *
     * Avoid using this one. If you need a green icon/border color,
     * use `colors.successBright` instead.
     */
    callout: green200,
    /**
     * Text color on regular backgrounds (e.g. Panel).
     * Used to indicate success UI.
     */
    successAccent: green100,
    /**
     * Icon and border colors. Elements that do not contain content.
     * Used to indicate success UI.
     */
    successBright: green200,
    /**
     * Background and text elements that render specifically in the
     * Webflow Designer canvas area.
     * Used to indicate success UI.
     */
    successAlt: green300,
    /**
     * Background for interactive elements (e.g Button).
     * Used to indicate success UI. Also handles :active state.
     */
    successBackground: green400,
    /**
     * Avoid using this one.
     * Use `colors.successBackground` instead.
     *
     * Background for interactive elements (e.g Button).
     * Used to indicate success UI. Also handles :active state.
     */
    success: green400,
    /**
     * Text color on regular backgrounds (e.g. Panel).
     * Icon and border colors. Elements that do not contain content.
     *
     * Background and text elements that render specifically in the
     * Webflow Designer canvas area.
     *
     * Used to indicate warning UI.
     */
    warning: yellow300,
    /**
     * Background for interactive elements (e.g Button).
     * Used to indicate warning UI. Also handles :active state.
     */
    warningBackground: yellow500,
    /**
     * Avoid using this one.
     * Use `colors.warning` instead.
     *
     * Renders the tooltip warning icon.
     */
    warningAlt: orange100,
    /**
     * Text color on regular backgrounds (e.g. Panel).
     * Used to indicate danger UI.
     */
    dangerAccent: red100,
    /**
     * Icon and border colors. Elements that do not contain content.
     * Used to indicate danger UI.
     */
    dangerBright: red200,
    /**
     * Background and text elements that render specifically in the
     * Webflow Designer canvas area. Used to indicate danger UI.
     */
    danger: red300,
    /**
     * Background for interactive elements (e.g Button).
     * Used to indicate danger UI. Also handles :active state.
     */
    dangerBackground: red400,
    /**
     * Icon and border colors. Elements that do not contain content.
     * Used for Symbols in Webflow.
     *
     * @see https://university.webflow.com/lesson/symbols
     */
    symbolAccent: tokens.colors.greenIcon,
    symbolNavigatorText: tokens.colors.greenText,
    /**
     * Background for interactive elements (e.g Button).
     * Used for Symbols in Webflow. Also handles :active state.
     *
     * @see https://university.webflow.com/lesson/symbols
     */
    symbolBackground: tokens.colors.greenTransparent,
    /**
     * Background for component instance label
     *
     * @see https://university.webflow.com/lesson/symbols
     */
    symbolInstance: green100,
    /**
     * Icon and border colors. Elements that do not contain content.
     * Used for Symbols in Webflow.
     *
     * Use `colors.symbolAccent` instead.
     *
     * @see https://university.webflow.com/lesson/symbols
     */
    symbolAccentAlt: green200,
    /**
     * Text color on regular backgrounds (e.g. Panel).
     * Used to indicate data UI within the Webflow Designer (e.g. Logic or CMS).
     *
     * @see https://webflow.com/logic
     */
    dynamicAccent: purple100,
    /**
     * Icon and border colors. Elements that do not contain content.
     * Used to indicate data UI within the Webflow Designer (e.g. Logic or CMS).
     *
     * @see https://webflow.com/logic
     */
    dynamicBright: tokens.colors.purpleIcon,
    dynamicNavigatorText: tokens.colors.purpleText,
    /**
     * Background and text elements that render specifically in the
     * Webflow Designer canvas area.
     * Used to indicate data UI within the Webflow Designer (e.g. Logic or CMS).
     *
     * @see https://webflow.com/logic
     */
    dynamicBackgroundAlt: purple300,
    /**
     * Background for interactive elements (e.g Button).
     * Used to indicate data UI within the Webflow Designer (e.g. Logic or CMS).
     * Also handles :active state.
     *
     * @see https://webflow.com/logic
     */
    dynamicBackground: tokens.colors.purpleTransparent,
    /**
     * Avoid using this one.
     * Use `colors.dynamicAccent` instead.
     * Currently used only in breadcrumbs.
     *
     * Text color on regular backgrounds (e.g. Panel).
     * Used to indicate data UI within the Webflow Designer (e.g. Logic or CMS).
     *
     * @see https://webflow.com/logic
     */
    dynamicAccentAlt: purple100,
    /**
     * Legacy usage.
     * Use `colors.dynamicBackground` instead.
     */
    dynamicAlt: pink400,
    /**
     * Avoid using this one.
     *
     * Current usage is not yet refined.
     */
    auxiliary: pink300,
    /**
     * Default background color for most surfaces.
     * Used primary in the Webflow Design panels.
     */
    background: tokens.colors.background1,
    /**
     * Alternative background color for most surfaces.
     * Used to create some contrast against the main background color.
     */
    backgroundDeepAlt: tokens.colors.background4,
    /**
     * Alternative background color for some surfaces.
     * Used primary in the Webflow Design panels as well as inputs.
     */
    backgroundDeep: tokens.colors.background1,
    /**
     * Alternative background color for a few surfaces.
     * Used to create maximum contrast against the main background color.
     */
    backgroundDark: tokens.colors.background1,
    /**
     * Alternate surface for some surfaces.
     */
    backgroundVividest: tokens.colors.background5,
    /**
     * Alternate surface for some surfaces.
     * Often used as an interactive (:hover) color.
     */
    backgroundVivid: tokens.colors.background1,
    /**
     * Avoid using this one.
     * Use `colors.backgroundDeepAlt` instead.
     *
     * Alternative background color for some surfaces.
     * Used in certain the Webflow Design modals.
     */
    backgroundDeepAltest: tokens.colors.background1,
    /**
     * Avoid using this one.
     * Use `colors.backgroundDark` instead.
     *
     * Alternative background color for a few surfaces.
     * Used to create maximum contrast against the main background color.
     */
    backgroundDeepest: tokens.colors.background1,
    /**
     * Avoid using this one.
     * Use `colors.backgroundDeep` instead.
     */
    backgroundDeepAlt2: tokens.colors.background1,
    /**
     * Avoid using this one.
     */
    backgroundLight: tokens.colors.background5,
    /**
     * Avoid using this one.
     */
    backgroundMiniNote: whiteFade05,
    /**
     * Avoid using this one.
     */
    backgroundSubtleWhite: whiteFade05,
    /**
     * Avoid using this one.
     */
    backgroundSubtleBlack: blackFade05,
    /**
     * Avoid using this one.
     */
    backgroundLightTransparent: whiteFade20,
    /**
     * Avoid using this one.
     */
    backgroundTransparentMask: 'transparent',
    /**
     * Avoid using this one.
     */
    backgroundGhostDefault: tokens.colors.uiBackgroundHover as string,
    /**
     * Avoid using this one.
     */
    backgroundGhostPrimary: blueFade10,
    /**
     * Avoid using this one.
     */
    backgroundGhostDanger: redFade10,
    /**
     * Avoid using this one.
     */
    backgroundDarkTransparent: 'rgba(0,0,0,0.2)',
    /**
     * Avoid using this one.
     *
     * Used for backdrops (overlays) for dialog elements (e.g. Modal).
     */
    backgroundMask: tokens.colors.uiPopoverBackdrop as string,
    /**
     * Default border color for most surfaces.
     */
    border: tokens.colors.border2,
    /**
     * Alternate border color for some surfaces.
     * Used in certain panels in the Webflow Designer.
     */
    borderDeep: tokens.colors.border1,
    /**
     * Alternate border color for some surfaces.
     */
    borderVivid: tokens.colors.border3,
    /**
     * Avoid using this one.
     */
    borderWithOpacity: 'rgba(51, 51, 51, 0.3)',
    /**
     * Avoid using this one.
     */
    borderLightTransparent: whiteFade90,
    /**
     * Avoid using this one.
     * Use `colors.borderDeep` instead.
     */
    borderDeepest: tokens.colors.border1,
    /**
     * Default text and icon color for most surfaces.
     */
    foreground: tokens.colors.textSecondary,
    foregroundNavigator: tokens.colors.textSecondary,
    /**
     * Avoid using this one.
     */
    foregroundAlphaDim: tokens.colors.textSecondary,
    /**
     * Alternate text and icon color for most surfaces.
     * Used for secondary/muted text.
     */
    foregroundDim: tokens.colors.textSecondary,
    /**
     * Alternate text and icon color for most surfaces.
     * Used for disabled/placeholder/very muted text.
     */
    foregroundDimmer: tokens.colors.textSecondary,
    /**
     * Used for heading text.
     */
    foregroundHeading: tokens.colors.textPrimary,
    /**
     * Alternate text and icon color for most surfaces.
     * Used for control text and icons (e.g. Button).
     */
    foregroundVivid: tokens.colors.textPrimary,
    /**
     * Avoid using this one.
     * Use `colors.foregroundDimmer` instead.
     */
    foregroundDimmerLess: tokens.colors.textSecondary,
    /**
     * Avoid using this one.
     * Use `colors.foregroundVivid` instead.
     */
    foregroundAlt: tokens.colors.textPrimary,
    /**
     * Avoid using this one.
     */
    foregroundDark: tokens.colors.textInverse,
    /**
     * Avoid using this one.
     * Use `colors.foregroundDimmer` instead.
     */
    foregroundControl: tokens.colors.actionSecondaryText,
    /**
     * Avoid using this one.
     * Use `colors.foreground` instead.
     */
    foregroundSlightlyDim: tokens.colors.textSecondary,
    /**
     * Background color for controls (e.g. Button).
     */
    control: tokens.colors.actionSecondaryBackground, // button
    /**
     * Background hover color for controls (e.g. Button).
     */
    controlHover: tokens.colors.actionSecondaryBackgroundHover, // button
    /**
     * Avoid using this one.
     * Currently being used for sliders.
     *
     * Use `colors.foreground` instead.
     */
    controlVivid: tokens.colors.textPrimary,
    brightShadow: 'rgba(128, 128, 128, 0.8)',
    hardShadow: tokens.colors.uiModalBackdrop as string,
    softShadow: blackFade30,
    mediumShadow: blackFade48,
    borderError: red400,

    canvas: {
      margin: blue300,
      negativeMargin: orange300,
      padding: green300,
    },

    cms: {
      staged: orange100,
      published: green200,
      archived: gray200,
    },

    logicFlows: {
      status: {
        disabled: red300,
        draft: orange100,
        on: green100,
        off: orange100,
      },
      foregroundDim: gray100,
      foregroundDimAlt: gray200,
      backgroundVivid: purple400,
      blockBorderColor: gray900, // border for Logic Flow blocks
      foregroundDimmer: gray200,
      statusIconBackground: '#524218',
      usersIconBackground: orange300,
      flowCanvasBackground: tokens.colors.background2,
    },

    properties: {
      local: {
        foreground: tokens.colors.blueText,
        icon: tokens.colors.blueText,
        background: tokens.colors.blueTransparent,
        backgroundHover: tokens.colors.blueTransparentHover,
      },
      inherited: {
        foreground: tokens.colors.orangeText,
        icon: tokens.colors.orangeText,
        background: tokens.colors.orangeTransparent,
        backgroundHover: tokens.colors.orangeTransparentHover,
      },
      default: {
        foreground: tokens.colors.textSecondary,
        icon: 'transparent',
        background: 'transparent',
        backgroundHover: 'transparent',
      },
      overridden: {
        foreground: tokens.colors.textPrimary,
        icon: tokens.colors.redText,
        background: tokens.colors.redTransparent,
        backgroundHover: tokens.colors.redTransparentHover,
      },
      id: {
        foreground: tokens.colors.pinkText,
        icon: tokens.colors.pinkText,
        background: tokens.colors.pinkTransparent,
        backgroundHover: tokens.colors.pinkTransparentHover,
      },
    },

    logoWhite: white, // white logo in modal
    upgradeBackground: pink600,

    /**
     * Colors below are deprecated.
     * Please use the ones defined above.
     */
    highlight: yellow400,
    focus: blue200, // outdated - should use `accent` instead
    primaryMuted: blue100, // outdated - should use `accentVivid` instead
    mutedAccent: blue400, // outdated - should use `accentDark` instead
    mutedSuccess: green200, // outdated - should use `successAccent` instead
    highlightBorder: gray400, // outdated - should use `borderVivid` instead
    controlSecondary: tokens.colors.background4, // outdated - was used for button gradients. no more gradients yo
    controlBackground: tokens.colors.background3, // outdated - should use `control` instead
    deepBackground: gray900, // outdated - should use `borderDeep` for borders and `backgroundDark` for backgrounds
    muted: gray200, // outdated - should use `foregroundDim` instead
    disabled: gray300, // outdated - should use `foregroundDimmer` instead
    vivid: gray50, // outdated - should use `foregroundVivid` instead
    highlightBackground: gray500, // outdated - should use `backgroundVivid` instead
    designerBackground: gray600, // outdated - should use `background` instead
    altBackground: gray600, // outdated - should use `background` instead
    faded: gray500, // outdated - should use `backgroundVivid` instead
  } as const;

  const focusColors = {
    outline: '0 0 0 1px ' + colors.accent,
    outlineThick: '0 0 0 2px ' + colors.accent,
    outlineAlt: '0 0 0 1px' + orange200,
    errorShadow: '0 0 0 1px ' + colors.danger,
    errorTextColor: colors.dangerAccent,
    warningShadow: '0 0 0 1px ' + colors.warningBackground,
    warningTextColor: colors.warning,
  } as const;

  const shadows = {
    // Outdated. Needs to be removed.
    depth: 'none',
    small: shadow(0, 0, 0, 1, 'rgba(0, 0, 0, 0.15)'),
    medium: shadow(0, 2, 2, 0, 'rgba(0, 0, 0, 0.25)'),
    large: shadow(0, 2, 4, 0, 'rgba(0, 0, 0, 0.25)'),
    modal: shadow(0, 1, 15, 0, colors.softShadow),
    modalDark: shadow(0, 8, 24, 0, colors.softShadow),
    border: shadow(0, 0, 0, 1, colors.softShadow),
    selected: [
      shadow(0, 0, 0, 2, colors.foregroundVivid),
      shadow(0, 0, 0, 3, colors.softShadow),
      shadow('inset', 0, 0, 0, 1, colors.softShadow),
    ].join(','),
  } as const;

  const gutters = {
    x0: 0,
    x4: 4,
    x8: 8,
    x12: 12,
    x16: 16,
    x20: 20,

    base: 4,
    narrow: 6,
    normal: 8,
    wide: 10,
  } as const;

  const filters = {
    dropShadow: {
      subtle: `drop-shadow(0 1px 1px ${black}`,
      contentBelow: `-${gutters.base}px 0 3px ${colors.backgroundDeep}`,
    },
    lighten: 'brightness(1.1)',
    darken: 'brightness(.95) saturate(1.1)',
  } as const;

  const control = {
    heightNarrow: 24,
    height: 24,
    heightLarge: 24,
    lineHeight: '22px',
    heightModalHeader: 42,
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 4,
    borderRadiusMiniNote: 4,
    borderRadiusModal: 4,
    borderRadiusPill: 10,
    borderRadiusDot: 5,
    backgroundColor: colors.control,
    backgroundColorHover: colors.controlHover,
    textColor: white, // for colored controls. otherwise use colors.foreground
    hoverTextColor: white,
    borderColor: tokens.colors.border1,
  } as const;

  const opacity = {
    disabled: 0.5,
    dragLayer: 0.5,
  } as const;

  const animation = {
    duration: '100ms',
    easing: 'ease',
  } as const;

  const card = {
    borderRadius: 4,
    /** Added 0px to pass falsy check */
    borderWidth: '0px',
  } as const;

  const dropdown = {
    backgroundColor: tokens.colors.background3,
    backgroundColorFade: colors.backgroundVivid,
    backgroundHoverColorFade: tokens.colors.background3,
    backgroundHeader: 'transparent',
    colorHeader: tokens.colors.textSecondary,
    groupVerticalPadding: 4,
    dividerWidth: 1,
    dividerColor: tokens.colors.border1,
    horizontalPadding: gutters.x8,
    horizontalViewportPadding: gutters.x8,
  } as const;

  const scrollView = {
    shadowColor: blacken(0.5),
    shadowSize: 3,
  } as const;

  const scrollbar = {
    width: gutters.base * 2,
    widthLarge: gutters.base * 3,
    trackColor: 'rgba(0, 0, 0, 0)',
    trackColorHightlight: 'rgba(0, 0, 0, 0.25)',
    thumbWidth: gutters.base,
    thumbColor: 'rgba(0, 0, 0, 0)',
    thumbColorWhileHoveringTrack: paletteNormal.colors.d2.whiteA30,
    thumbColorHightlight: paletteNormal.colors.d2.whiteA30,
    thumbColorHightlightWhileHoveringThumb: paletteNormal.colors.d2.whiteA40,
    thumbBorderRadius: gutters.base / 2,
    thumbBorderRadiusLarge: gutters.base,
  } as const;

  const popover = {
    backgroundColor: tokens.colors.background2,
    backgroundColorDark: tokens.colors.background2,
    boxShadow: tokens.boxShadows.menu,
  } as const;

  const tooltip = {
    alternateColor: tokens.colors.textSecondary,
    alternateBackgroundColor: tokens.colors.background3,
    hoverBackgroundColor: white,
    textColor: white,
    statefulTextColor: white,
    error: {
      textColor: white,
      backgroundColor: colors.dangerBackground,
    },
    filterShadow: [
      'drop-shadow(0 0.5px 0 rgba(0,0,0,0.6))',
      'drop-shadow(0 2px 6px rgba(0,0,0,0.18))',
      'drop-shadow(0 4px 8px rgba(0,0,0,0.12))',
      'drop-shadow(0 8px 16px rgba(0,0,0,0.08))',
      'drop-shadow(0 12px 25px rgba(0,0,0,0.06))',
    ].join(' '),
    filterShadowLight: [
      'drop-shadow(0 0.5px 0 rgba(0,0,0,0.6))',
      'drop-shadow(0 2px 6px rgba(0,0,0,0.18))',
      'drop-shadow(0 4px 8px rgba(0,0,0,0.12))',
      'drop-shadow(0 8px 16px rgba(0,0,0,0.08))',
      'drop-shadow(0 12px 25px rgba(0,0,0,0.06))',
    ].join(' '),
  } as const;

  const progressbar = {
    height: 8,
  } as const;

  const Select = {
    controlBorderWidth: 0,
    error: {
      boxShadow: `0 0 2px 1px ${fade(0.12)(colors.borderError)}`,
      borderType: 'solid',
      borderWidth: 1,
      borderColor: colors.borderError,
    },
    itemBackgroundColorHover: tokens.colors.uiBackgroundHover as string,
  } as const;

  const Button = {
    size: {
      large: 24,
      small: 24,
    },
    gutters: {
      large: gutters.x8,
      small: gutters.x8,
    },
    fontSize: {
      large: bodyFontSize,
      small: bodyFontSize,
    },
    boxShadow: tokens.boxShadows.actionSecondary,
    color: tokens.colors.actionSecondaryText,
    borderWidth: 0,
    paddingRight: gutters.base,
    paddingLeft: gutters.base,
    padding: gutters.base,
    primary: {
      background: tokens.colors.actionPrimaryBackground,
      backgroundHover: tokens.colors.actionPrimaryBackgroundHover,
      borderColor: 'transparent',
      color: tokens.colors.actionPrimaryText,
    },
    danger: {
      background: tokens.colors.redBackground,
      backgroundHover: tokens.colors.redBackgroundHover,
      borderColor: 'transparent',
      color: tokens.colors.actionPrimaryText,
    },
    dynamic: {
      background: tokens.colors.purpleTransparent,
      backgroundHover: tokens.colors.purpleTransparentHover,
      borderColor: 'transparent',
      color: tokens.colors.purpleText,
    },
    emphasis: {
      background: tokens.colors.greenBackground,
      backgroundHover: tokens.colors.greenBackgroundHover,
      borderColor: 'transparent',
      color: tokens.colors.actionPrimaryText,
    },
    risk: {
      background: tokens.colors.redBackground,
      backgroundHover: tokens.colors.redBackgroundHover,
      borderColor: 'transparent',
      color: tokens.colors.actionPrimaryText,
    },
    subtle: {
      background: 'transparent',
      backgroundHover: 'transparent',
      borderColor: tokens.colors.border1,
      borderColorHover: tokens.colors.actionSecondaryBorderHover,
      boxShadow: 'none',
      color: tokens.colors.actionSecondaryText,
      borderWidth: 1,
      borderStyle: 'solid',
    },
    warning: {
      background: tokens.colors.orangeBackground,
      backgroundHover: tokens.colors.orangeBackgroundHover,
      borderColor: 'transparent',
      color: tokens.colors.actionPrimaryText,
    },
    blend: {
      backgroundHover: tokens.colors.uiBackgroundHover,
    },
    hover: {
      default: tokens.colors.actionSecondaryTextHover,
      color: tokens.colors.actionPrimaryTextHover,
    },
    selected: {
      ghostBackground: tokens.colors.background2,
    },
    disabled: {
      opacity: opacity.disabled,
      borderColor: gray700 as string | null | undefined,
      boxShadow: 'none' as string | null | undefined,
    },
    height: 24,
    buttonShellHeight: control.height,
  } as const;

  const ButtonGroup = {
    gap: '1px',
    background: control.backgroundColor,
    borderRadius: control.borderRadius,
    boxShadow: Button.boxShadow,
  } as const;

  const Slider = {
    background: tokens.colors.border1,
  } as const;

  const TextInput = {
    background: tokens.colors.uiInputBackground,
    borderColor: tokens.colors.border2,
    borderColorHover: tokens.colors.border3,
    boxShadow: tokens.boxShadows.inputInner,
    color: tokens.colors.textPrimary,
    hover: {
      background: tokens.colors.uiInputBackground as string,
    },
    focus: {
      background: tokens.colors.uiInputBackground as string,
    },
    disabled: {
      background: colors.backgroundDeep,
      color: gray200,
    },
  } as const;

  const TextInputDark = {
    focus: {
      background: black,
    },
    disabled: {
      background: gray900,
    },
  } as const;

  const UnitInput = {
    background: 'transparent',
    backgroundHover: 'transparent',
    boxShadow: '0 0 0 rgba(0,0,0,0)',
  } as const;

  const FinderInput = {
    border: `${control.borderWidth}px solid ${colors.borderDeep}`,
  } as const;

  const InputWithSearch = {
    iconColor: gray300,
  } as const;

  const Divider = {
    backgroundColor: gray200,
    vivid: {
      backgroundColor: gray500,
    },
    subtle: {
      backgroundColor: whiteFade05,
    },
    dark: {
      backgroundColor: gray900,
    },
  } as const;

  const Modal = {
    background: tokens.colors.background1,
    backgroundDark: tokens.colors.background1,
    borderRadius: control.borderRadiusModal,
    closeButtonWidth: 24,
  } as const;

  const Text = {
    headingColor: colors.foregroundVivid,
    labelColor: colors.foregroundVivid,
  } as const;

  const topBar = {
    borderBottom: `1px solid ${colors.borderDeep}`,
    breakpointTipTitleColor: tokens.colors.textPrimary,
    breakpointTipSubtitleColor: tokens.colors.textSecondary,
  } as const;

  const canvasBar = {
    breadcrumbBackground: tokens.colors.background1,
    breadcrumbBackgroundHover: tokens.colors.background2,
    chevronFill: tokens.colors.border2,
  } as const;

  const assetsTab = {
    actionBackgroundColor: tokens.colors.background1,
    actionBorderTop: `${control.borderWidth}px solid ${colors.borderDeep}`,
    buttonHeight: gutters.base * 8,
    imageBackground: 'rgba(255, 255, 255, 0.12)',
  } as const;

  const Tab = {
    textColor: tokens.colors.textSecondary,
    textColorHover: tokens.colors.textPrimary,
  } as const;

  const TableControl = {
    color: blue100,
    iconMargin: gutters.x4,
    alternate: {
      isButton: false,
    },
  } as const;

  const PropList = {
    backgroundColor: tokens.colors.background1,
    borderColor: tokens.colors.border1,
    backgroundHover: tokens.colors.background1,
    lineHeight: '16px',
    fontSize: bodyFontSize,
    item: {
      paddingTop: gutters.x8,
      paddingRight: gutters.x12,
      paddingBottom: gutters.x8,
      paddingLeft: gutters.x12,
    },
  } as const;

  const Table = {
    header: {
      paddings: {
        paddingRight: gutters.x12,
        paddingLeft: gutters.x12,
        paddingTop: gutters.x8,
        paddingBottom: gutters.x8,
      },
    },
    row: {
      paddings: {
        paddingRight: gutters.x12,
        paddingLeft: gutters.x12,
        paddingTop: gutters.x12,
        paddingBottom: gutters.x12,
      },
      hover: {
        background: tokens.colors.uiBackgroundHover as string,
        color: tokens.colors.textPrimary,
        iconWidth: 40,
      },
      active: {
        background: tokens.colors.background3,
      },
      selected: {
        background: tokens.colors.background3,
      },
    },
  } as const;

  const ExpandableTable = {
    row: {
      hover: {
        iconWidth: 40,
      },
    },
  } as const;

  const Suggestion = {
    background: 'transparent',
    backgroundHover: 'transparent',
  } as const;

  const InteractionsTimeline = {
    borderColor: colors.borderDeep,
    borderColorHighlight: colors.accentPrimary,

    TimeLabelDot: {
      borderColor: tokens.colors.background1,
      backgroundColor: tokens.colors.uiBorderSolid as string,
      backgroundColorHighlight: colors.accentPrimary,
    },
  } as const;

  const TreeModal = {
    treeBackground: colors.backgroundDeepAlt,
    borderColor: colors.backgroundDeep,
    rowHeight: 32,
    searchMarginBottom: gutters.x8,
  } as const;

  const TreeList = {
    nodeBackground: tokens.colors.uiBackgroundHover as string,
    offsetLeft: -gutters.x8,
    background: 'transparent',
  } as const;

  const lineHeights = {
    oneline: 1,
    text: 1.4,
    // heading: 1.25, // commented as this key is duplicated by heading below

    // TODO: Kill these constants and use unitless line-heights in the typography object.
    copy: 16,
    subheading: 18,
    heading: 20,
  } as const;

  const fontSizes = {
    // TODO: Kill these constants and use fontsizes defined in the typography object.
    finePrint: 10, // we shouldn't use 10px fontsize anywhere
    copy: bodyFontSize,
    copyLarge: headingFontSize,
    subheading: headingFontSize,
    heading: 14,
  } as const;

  const sansSerifFontStack =
    'Inter ,-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", sans-serif';

  const typography = {
    title: {
      fontSize: titleFontSize,
      fontFamily: sansSerifFontStack,
      lineHeight: 16,
      fontWeight: 600,
    },
    heading: {
      fontSize: headingFontSize,
      fontFamily: sansSerifFontStack,
      lineHeight: 16,
      fontWeight: 600,
    },
    subheading: {
      fontSize: headingFontSize,
      fontFamily: sansSerifFontStack,
      lineHeight: 16,
      fontWeight: 600,
    },
    copy: {
      fontSize: bodyFontSize,
      fontFamily: sansSerifFontStack,
      lineHeight: 16,
      fontWeight: 400,
    },
    finePrint: {
      fontSize: bodyFontSize,
      fontFamily: sansSerifFontStack,
      lineHeight: 16,
      fontWeight: 400,
    },
    label: {
      fontSize: bodyFontSize,
      fontFamily: sansSerifFontStack,
      fontWeight: 400,
      lineHeight: '16px',
      letterSpacing: bodyLetterSpacing,
    },
    labelSmall: {
      /**
       * Adjustments to ensure that the label fits on one line within
       * the Style Panel.
       *
       * 2023-09-21
       */
      fontSize: 11,
      fontFamily: sansSerifFontStack,
      fontWeight: 400,
      lineHeight: '16px',
      letterSpacing: '-0.2px',
    },
    labelNarrow: {
      /**
       * Adjustments to ensure that the label fits on one line within
       * the Style Panel.
       *
       * 2023-09-21
       */
      fontSize: 11,
      fontFamily: sansSerifFontStack,
      fontWeight: 400,
      lineHeight: '16px',
      letterSpacing: '-0.4px',
    },
    labelTiny: {
      fontSize: 9,
      fontFamily: sansSerifFontStack,
      lineHeight: '12px',
      letterSpacing: bodyLetterSpacing,
      textTransform: 'none',
    },
    labelLarge: {
      fontSize: headingFontSize,
      fontFamily: sansSerifFontStack,
      fontWeight: 600,
      lineHeight: '16px',
    },
  } as const;

  /**
   * These have to stay as hex values (not CSS variables) due to the
   * complex ways they are computed (opacity) for Grid interactions.
   *
   * @see public/js/designer-flux/components/ui/GridOnCanvasProbes.jsx
   *
   * 2023-09-07
   */
  const gridColors = {
    accent: {background: blue200, color: white},
    red: {background: red200, color: white},
    blue: {background: blue300, color: white},
    pink: {background: pink300, color: white},
    green: {background: green300, color: white},
    black: {background: gray800, color: gray50},
    white: {background: white, color: gray600},
  } as const;

  const gridEditButtonColors = {
    // TODO: add these colors
    blue: {background: '#CCE3FA', color: blue300},
    orange: {background: '#FDD6BF', color: orange300},
  } as const;

  const EmptyState = {
    background: tokens.colors.uiCardBackground as string,
    hoverBackgroundColor: tokens.colors.uiCardBackground as string,
    border: 'transparent',
    borderHover: 'transparent',
    padding: 6 * gutters.x4,
    heading: {
      fontSize: typography.heading.fontSize,
    },
  } as const;

  const fontWeights = {
    copy: 400,
    subheading: 600,
    heading: 600,
  } as const;

  const fontFamilies = {
    copy: sansSerifFontStack,
    subheading: sansSerifFontStack,
    heading: sansSerifFontStack,
    monospace:
      '"SF Mono","Liberation Mono", Inconsolata, Consolas, Monaco, "Andale Mono", monospace',
  } as const;

  const list = {
    background: tokens.colors.background1,
    gridBase: gutters.base * 3,
    gridBorder: 'none',
    gridItemBackgroundHover: tokens.colors.uiBackgroundHover as string,
    itemBackgroundHover: tokens.colors.uiBackgroundHover as string,
    itemBackgroundSelected: tokens.colors.uiBackgroundActive as string,
    itemSelectedBackground: tokens.colors.blueTransparent,
    itemSelectedBackgroundHover: tokens.colors.blueTransparentHover,
    itemHeight: gutters.base * 6,
    padding: gutters.base * 6,
    branchColor: tokens.colors.borderDefault,
    actionIconWidth: 24,
    rowHeight: gutters.base * 12,
    panelItemHeight: gutters.base * 8,
    panelItemHeightLarge: gutters.base * 10,
    propertyBackground: 'transparent',
    propertyBorderColor: tokens.colors.border1,
    propertyItemBackground: 'transparent',
    propertyItemBackgroundHover: tokens.colors.uiBackgroundHover as string,
    propertyIconHeight: 16,
    sectionHeight: 40,
    sectionPadding: gutters.base * 3,
    sectionLargePadding: gutters.base * 2,
  } as const;

  const StyledSmallMarkIcon = (props: any) => (
    <SmallMarkIcon
      {...props}
      style={{
        position: 'relative',
        bottom: 2,
        ...props.style,
      }}
    />
  );

  const checkbox = {
    borderRadius: 2,
    size: control.height / 2,
    icons: {
      checkmark: StyledSmallMarkIcon,
      dashmark: DashIcon,
    },
    borderColor: {
      normal: tokens.colors.uiCheckboxRadioBorderColor,
      checked: tokens.colors.actionPrimaryBackground,
      disabled: tokens.colors.uiCheckboxRadioBorderColor,
    },
    color: {
      hover: 'transparent',
      checked: tokens.colors.actionPrimaryText,
      dashed: tokens.colors.actionPrimaryText,
    },
    background: {
      normal: tokens.colors.actionSecondaryBackground,
      checked: tokens.colors.actionPrimaryBackground,
      focus: tokens.colors.actionSecondaryBackground,
      disabled: tokens.colors.actionSecondaryBackground,
    },
  } as const;

  // Outdated. Needs to be removed.
  const easingEditor = {
    lightGray: colors.backgroundVivid,
    midGray: colors.backgroundDeep,
  } as const;

  // Update instances with colors.etc variables used
  const protectedItems = {
    backgroundColor: colors.backgroundVivid,
    borderColor: colors.border,
  } as const;

  const itemsByCollectionModal = {
    backgroundColor: colors.backgroundVivid,
    borderColor: gray400,
  } as const;

  /**
   * 2022-04-05
   * Reverted to custom olive green, based on issues raised by Dylan + EDU team.
   * @see https://webflow.atlassian.net/browse/PDTY-2417
   *
   * 2022-03-16
   * Updated to orange, based on recommendations from Sergie.
   * @see https://github.com/webflow/webflow/pull/46086
   */
  const CSVTable = {
    headerBackgroundColor: tokens.colors.background2,
    headerBorderColor: tokens.colors.border1,
    borderColor: tokens.colors.border1,
    borderRadius: 4,
  } as const;

  const MiniNote = {
    background: gray500,
    color: gray50,
    fontSize: headingFontSize,
    borderWidth: 0,
    borderStyle: 'none',
    borderColor: 'transparent',
    accentStripeWidth: 2,
    stripeCoversBorder: false,
  } as const;

  const MiniSettings = {
    borderBottom: `1px solid ${tokens.colors.border1}`,
    titleColor: 'inherit',
  } as const;

  const ExternalActionIcon = (props: any) => (
    <ExternalLink
      {...props}
      style={{
        marginLeft: gutters.x4,
        ...props.style,
      }}
    />
  );

  const Note = {
    lineHeight: typography.copy.lineHeight,
    fontSize: typography.finePrint.fontSize,
    fontWeight: typography.copy.fontWeight,
    paddingTop: gutters.x8,
    paddingY: gutters.x4,
    paddingX: gutters.x8,
    colors: {
      neutral: colors.foreground,
      actionNeutral: colors.accentVivid,
      warning: colors.warning,
      success: colors.successAccent,
      error: colors.dangerAccent,
      border: colors.border,
      background: gray500,
    },
    iconSize: 16,
    externalActionIcon: ExternalActionIcon,
  } as const;

  const PropertyLabel = {
    buttonBackgroundActive: tokens.colors.background3,
  } as const;

  const zIndexes = {...Z_INDEXES} as const;

  const pageSettings = {
    expandGroup: {
      freestanding: false,
      borderWidth: 0,
    },
  } as const;

  const ShippingMethodDropdown = {
    noteLineCountByShippingMethodMode: {
      'flat-rate': 3,
      percentage: 3,
      price: 3,
      quantity: 3,
      weight: 3,
    },
    defaultNoteLineCount: 2,
  } as const;

  const CountryModal = {
    width: 480,
  } as const;

  const CMS = {
    fieldRowPadding: `${gutters.normal}px ${gutters.wide}px`,
    fieldRowBorder: `${gutters.base / 2}px solid ${colors.backgroundVividest}`,
    listItemHeight: 4 * 8,
  } as const;

  const Toggle = {
    selected: {
      background: colors.backgroundDeep,
    },
    disabled: null,
    hover: null as
      | {
          background: string;
        }
      | null
      | undefined,
  } as const;

  const ExternalAction = (props: any) => (
    <ExternalLink
      {...props}
      style={{
        marginLeft: gutters.x4,
        ...props.style,
      }}
    />
  );

  const TodoList = {
    padding: gutters.x12,
    textPadding: gutters.x12,
    backgroundColor: tokens.colors.background2,
    borderColor: tokens.colors.border1,
    actionText: {
      fontSize: typography.copy.fontSize,
      fontWeight: typography.copy.fontWeight,
      lineHeight: typography.copy.lineHeight,
      fontFamily: typography.copy.fontFamily,
      color: tokens.colors.blueText,
    },
    iconsStyle: {
      checkboxChecked: {
        flexShrink: 0,
        marginTop: 2,
      },
      checkboxUnchecked: {
        flexShrink: 0,
        color: gray300,
        marginTop: 2,
      },
      externalAction: {
        marginLeft: gutters.x4,
      },
    },
    borderBottomStyle: 'solid',
  } as const;

  const Well = {
    background: tokens.colors.background2,
  } as const;

  const FileList = {
    color: gray100,
    borderColor: gray800,
    backgroundColor: gray700,
    fontSize: typography.copy.fontSize,
    gridAutoRows: `${gutters.base * 8}px auto`,
    icon: {
      color: gray100,
      padding: `0px ${gutters.base * 2}px 0px ${gutters.base * 3}px`,
    },
  } as const;

  const CommerceGettingStartedChecklist = {
    width: '650px',
    displaySection: 'block',
    displayButton: 'none',
    borderBottomStyle: 'solid',
    paddingLeft: gutters.x12,
    paddingRight: gutters.x12,
    paddingTop: gutters.x16,
    checklistSectionPaddingBottom: gutters.x12 * 2,
    headingFontSize: typography.title.fontSize,
    titleMargin: 0,
    checklistPaddingBottom: 0,
    noteContainerPaddingTop: gutters.x12 * 2,
  } as const;

  const CommerceCheckoutSettingsWarningNote = {
    borderColor: colors.backgroundDeepAlt,
    icons: {
      externalAction: ExternalAction,
    },
  } as const;

  const CommercePaymentSettingsApplePayIcon = {
    color: white,
  } as const;

  const FilePreview = {
    fontSize: typography.finePrint.fontSize,
  } as const;

  const FilePreviewLarge = {
    fontSize: typography.copy.fontSize,
  } as const;

  const Collapsible = {
    horizontalPadding: gutters.base * 2,
    verticalPadding: gutters.base * 2,
    backgroundColor: 'transparent',
    borderWidth: 2,
    boxShadow: shadows.small,
  } as const;

  const CommerceProductOptions = {
    newOptionColor: tokens.colors.blueText,
    disabledOptionColor: tokens.colors.textSecondary,
  } as const;

  const OptionSetInput = {
    hoverColor: colors.backgroundDark,
  } as const;

  const OptionPill = {
    normal: {
      height: control.heightLarge - gutters.base,
      color: control.textColor,
      background: colors.accentBackground,
      border: 'none',
      boxShadow: shadows.small,
      hover: {
        color: white,
        background: tokens.colors.blueBackground,
        border: 'none',
      },
      padding: gutters.base / 2,
      margin: gutters.base / 2,
    },
    editing: {
      color: control.textColor,
      background: colors.control,
      border: 'none',
    },
    menuIndicator: {
      border: `1px solid ${tokens.colors.blueBorder}`,
    },
  } as const;

  const MiniImageMultiInput = {
    placeholderIconColor: colors.background,
    placeholderIconBackgroundColor: colors.foreground,
    width: 28,
    height: 28,
    filePopoverBtnWidth: 20,
    filePopoverBtnHeight: 28,
  } as const;

  const WebflowColorPicker = {
    borderColor: gray700,
  } as const;

  const MultiImagePreview = {
    fontSizes: {
      copy: bodyFontSize,
      subheading: headingFontSize,
      heading: 14,
    },
    lineHeight: '16px',
    headingColor: gray50,
    headingFontWeight: 600,
    borderRadius: 2,
    button: {
      size: 24,
      backgroundColor: blackFade48,
      color: gray50,
    },
    altInputHeight: 24,
  } as const;

  const WireFrameField = {
    field: {
      backgroundColor: gray500,
      border: {color: tokens.colors.borderDefault},
      hover: {
        backgroundColor: tokens.colors.blueTransparent,
        border: {color: rgba(blue100, 0.8)},
      },
    },
    icon: {
      color: gray100,
    },
  } as const;

  const IX2ClassSelectInput = {
    menuBackgroundColor: tokens.colors.uiMenuContentBackground,
    menuBorder: 'none',
    menuBorderRadius: 4,
    menuBoxShadow: tokens.boxShadows.menu,
    menuPadding: 0,
    menuItemBackgroundColorHover: tokens.colors.uiBackgroundHover as string,
    menuItemPadding: '2px 8px',
  } as const;

  const IX2PropListItem = {
    backgroundColor: tokens.colors.background1,
    backgroundColorHover: tokens.colors.uiBackgroundHover as string,
  } as const;

  const globalStyleTagStyles = `
    body {
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
    [data-wf-base-text-input]::selection {
      background-color: ${blue400};
      color: white;
    }
  `;

  const CanvasResizeHandle = {
    hoverColor: blue200,
  } as const;

  const counter = {
    color: colors.foregroundDark,
  } as const;

  const select = {height: undefined} as const;

  return {
    name: 'designer',
    globalStyleTagStyles,
    animation,
    filters,
    colors,
    focusColors,
    shadows,
    gutters,
    control,
    opacity,
    card,
    dropdown,
    scrollView,
    scrollbar,
    popover,
    tooltip,
    progressbar,
    list,
    lineHeights,
    fontSizes,
    typography,
    fontWeights,
    fontFamilies,
    zIndexes,
    easingEditor,
    checkbox,
    protectedItems,
    itemsByCollectionModal,
    CSVTable,
    Button,
    ButtonGroup,
    Select,
    pageSettings,
    MiniNote,
    MiniSettings,
    Note,
    PropertyLabel,
    Slider,
    TextInput,
    TextInputDark,
    UnitInput,
    FinderInput,
    Divider,
    Modal,
    Text,
    topBar,
    canvasBar,
    assetsTab,
    TableControl,
    PropList,
    Table,
    EmptyState,
    Suggestion,
    InteractionsTimeline,
    TreeList,
    TreeModal,
    gridColors,
    gridEditButtonColors,
    InputWithSearch,
    ShippingMethodDropdown,
    CountryModal,
    CMS,
    Toggle,
    TodoList,
    Well,
    FileList,
    CommerceGettingStartedChecklist,
    CommerceCheckoutSettingsWarningNote,
    CommercePaymentSettingsApplePayIcon,
    FilePreview,
    FilePreviewLarge,
    Collapsible,
    CommerceProductOptions,
    OptionSetInput,
    OptionPill,
    MiniImageMultiInput,
    WebflowColorPicker,
    ExpandableTable,
    MultiImagePreview,
    WireFrameField,
    CanvasResizeHandle,
    counter,
    Tab,
    IX2ClassSelectInput,
    IX2PropListItem,

    // TODO: This was in the editor theme; what should it be here?
    highlight: () => colors.highlight,

    // TODO: This was in the editor theme; what should it be here?
    table: {
      header: {
        row: {
          backgroundColor: colors.background,
          borderColor: colors.border,
          height: 32,
          fontSize: typography.copy.fontSize,
          fontWeight: typography.copy.fontWeight,
          fontFamily: typography.copy.fontFamily,
        },
        cell: {
          selected: colors.backgroundVivid,
        },
      },
      row: {
        height: 32,
        disabled: {
          backgroundColor: colors.background,
          color: colors.foreground,
        },
      },
    },
    select,
  };
};

const designerTheme: Theme = buildTheme(paletteNormal);

export default designerTheme;
